import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Calendar,
  ArrowRight,
  Eye,
  EyeOff,
  Trash2,
  ExternalLink,
  Image as ImageIcon,
  Edit3,
  Save,
  XCircle,
  Sparkles,
  Loader2,
  CheckCircle2,
  Circle,
  Wrench,
  Bug,
} from 'lucide-react';
import { FixDiffModal } from './FixDiffModal';
import type { FixDiffTask } from '@/hooks/useFixDiffTasks';
import { formatDistanceToNow } from 'date-fns';
import { setDiffPublished } from '@/utils/externalDiffsAPI';
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { formatTagLabel, getTagColor, type PricingEventTag } from '@/types/pricingEventTags';

// Helper function to format category names for display
const formatCategoryLabel = (category: string | null | undefined): string => {
  if (!category || category === 'null') return 'null';

  // Special cases
  if (category === 'pricing_structure') return 'Pricing Structure';

  // General formatting: replace underscores with spaces and capitalize each word
  return category
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Helper function to get unique color for each category
const getCategoryColor = (category: string | null | undefined): string => {
  if (!category || category === 'null') return 'bg-red-100 text-red-800 border-red-300';

  const colorMap: Record<string, string> = {
    'pricing_structure': 'bg-purple-100 text-purple-800 border-purple-300',
    'feature': 'bg-blue-100 text-blue-800 border-blue-300',
    'limits': 'bg-orange-100 text-orange-800 border-orange-300',
    'misc': 'bg-gray-100 text-gray-800 border-gray-300',
    'pricing': 'bg-green-100 text-green-800 border-green-300',
    'metric': 'bg-teal-100 text-teal-800 border-teal-300',
    'discount': 'bg-pink-100 text-pink-800 border-pink-300',
    'plan': 'bg-indigo-100 text-indigo-800 border-indigo-300',
  };

  return colorMap[category.toLowerCase()] || 'bg-slate-100 text-slate-800 border-slate-300';
};

interface DiffCardProps {
  diff: any;
  onOpenLightbox: (diffId: string, eventId: string | undefined, companySlug: string, events: any[]) => void;
  onRefresh: () => void;
  onEdit?: () => void; // Callback before navigating to edit page (for setting navigation list)
  selectedEventTypes?: string[];
  allEnrichedEvents?: any[]; // All enriched events for lightbox navigation
  fixDiffTask?: FixDiffTask | null; // Active fix_diff task for this diff
}

type EditingField = 'hook' | 'news_description' | 'event_description';

interface EditingState {
  field: EditingField;
  newsId?: string;
  eventId?: string;
  value: string;
}

export const DiffCard: React.FC<DiffCardProps> = ({ diff, onOpenLightbox, onRefresh, onEdit, selectedEventTypes = [], allEnrichedEvents, fixDiffTask }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteEventDialogOpen, setDeleteEventDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<any>(null);
  const [fixDiffModalOpen, setFixDiffModalOpen] = useState(false);

  // Inline editing state
  const [editingState, setEditingState] = useState<EditingState | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isAIProcessing, setIsAIProcessing] = useState(false);
  const [aiInstructions, setAiInstructions] = useState('');
  const [isAIRewriting, setIsAIRewriting] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const editContainerRef = useRef<HTMLDivElement>(null);

  // Optimistic updates - store local overrides
  const [localHook, setLocalHook] = useState<string | null>(null);
  const [localNewsDescription, setLocalNewsDescription] = useState<string | null>(null);
  const [localEventDescriptions, setLocalEventDescriptions] = useState<Map<string, string>>(new Map());
  const [localVerifiedEvents, setLocalVerifiedEvents] = useState<Map<string, boolean>>(new Map());
  const [localDeletedEventIds, setLocalDeletedEventIds] = useState<Set<string>>(new Set());
  const [verifyAllDialogOpen, setVerifyAllDialogOpen] = useState(false);

  const formatRelativeTime = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return dateString;
    }
  };

  const formatVersionDate = (version: string): string => {
    const dateMatch = version.match(/(\d{8})/);
    if (dateMatch) {
      const dateStr = dateMatch[1];
      const year = parseInt(dateStr.substring(0, 4));
      const month = parseInt(dateStr.substring(4, 6));
      const day = parseInt(dateStr.substring(6, 8));

      // Create date object and format as "Month Day, Year"
      const date = new Date(year, month - 1, day);
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                          'July', 'August', 'September', 'October', 'November', 'December'];

      return `${monthNames[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
    }
    return version;
  };

  const formatVersionDateAbbreviated = (version: string): string => {
    const dateMatch = version.match(/(\d{8})/);
    if (!dateMatch) return '';

    const dateStr = dateMatch[1];
    const year = parseInt(dateStr.substring(0, 4));
    const month = parseInt(dateStr.substring(4, 6)); // 1-indexed from API
    const day = parseInt(dateStr.substring(6, 8));   // 1-indexed from API

    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Convert 1-indexed month to 0-indexed for array access
    return `${monthNames[month - 1]} ${day}, ${year}`;
  };

  // Check if any event in the diff has labels or annotation_coordinates
  const hasAnnotationData = (changes: any[]): boolean => {
    return (changes || []).some((event: any) =>
      (event.labels && event.labels.length > 0) ||
      (event.annotation_coordinates && Object.keys(event.annotation_coordinates || {}).length > 0)
    );
  };

  // Generate the test-diff-viewer URL
  const getTestDiffViewerUrl = (slug: string, vBefore: string, vAfter: string): string => {
    const beforeDate = vBefore.match(/(\d{8})/)?.[1] || vBefore;
    const afterDate = vAfter.match(/(\d{8})/)?.[1] || vAfter;
    return `https://pricingsaas.com/test-diff-viewer?slug=${slug}&before=${beforeDate}&after=${afterDate}`;
  };

  const handleTestDiffViewerPreview = (e: React.MouseEvent) => {
    e.stopPropagation();
    const previewUrl = getTestDiffViewerUrl(diff.slug, diff.v_before, diff.v_after);
    window.open(previewUrl, '_blank', 'noopener,noreferrer');
  };

  const handleTogglePublished = async (e: React.MouseEvent) => {
    e.stopPropagation();

    // Get non-deleted events
    const nonDeletedEvents = (diff.changes || []).filter((e: any) => !localDeletedEventIds.has(e.id));

    // If publishing (not unpublishing), check if any non-deleted events are verified
    if (!diff.published && nonDeletedEvents.length > 0) {
      const hasVerifiedEvents = nonDeletedEvents.some((event: any) =>
        localVerifiedEvents.get(event.id) ?? event.verified
      );

      if (!hasVerifiedEvents) {
        // No events are verified - show dialog
        setVerifyAllDialogOpen(true);
        return;
      }
    }

    await publishDiff();
  };

  const publishDiff = async () => {
    try {
      await setDiffPublished(diff.id, !diff.published);
      toast({
        title: "Success",
        description: `Diff ${!diff.published ? 'published' : 'unpublished'} successfully`,
      });
      onRefresh();
    } catch (error) {
      console.error('Error toggling published status:', error);
      toast({
        title: "Error",
        description: "Failed to toggle published status",
        variant: "destructive",
      });
    }
  };

  const handleVerifyAllAndPublish = async () => {
    try {
      // Verify all non-deleted events
      const nonDeletedEvents = (diff.changes || []).filter((e: any) => !localDeletedEventIds.has(e.id));
      const eventIds = nonDeletedEvents.map((e: any) => e.id);

      if (eventIds.length === 0) {
        toast({
          title: "Error",
          description: "No events to verify",
          variant: "destructive",
        });
        setVerifyAllDialogOpen(false);
        return;
      }

      const { error } = await supabase
        .from('app_events')
        .update({ verified: true })
        .in('id', eventIds);

      if (error) throw error;

      // Update local state
      setLocalVerifiedEvents(prev => {
        const newMap = new Map(prev);
        eventIds.forEach(id => newMap.set(id, true));
        return newMap;
      });

      // Close dialog and publish
      setVerifyAllDialogOpen(false);
      await publishDiff();
    } catch (error: any) {
      console.error('Error verifying events:', error);
      toast({
        title: "Error",
        description: `Failed to verify events: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  const handleToggleEventVerified = async (e: React.MouseEvent, eventId: string, currentVerified: boolean) => {
    e.stopPropagation();
    try {
      const newVerified = !currentVerified;
      const { error } = await supabase
        .from('app_events')
        .update({ verified: newVerified })
        .eq('id', eventId);

      if (error) throw error;

      // Optimistic update
      setLocalVerifiedEvents(prev => {
        const newMap = new Map(prev);
        newMap.set(eventId, newVerified);
        return newMap;
      });

      toast({
        title: "Success",
        description: `Event ${newVerified ? 'verified' : 'unverified'}`,
      });
    } catch (error: any) {
      console.error('Error toggling verified:', error);
      toast({
        title: "Error",
        description: `Failed to update: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  const handleDeleteEvent = async () => {
    if (!eventToDelete) return;

    // Optimistic update - hide event immediately
    setLocalDeletedEventIds(prev => {
      const newSet = new Set(prev);
      newSet.add(eventToDelete.id);
      return newSet;
    });

    // Close dialog immediately for snappy UX
    setDeleteEventDialogOpen(false);
    const deletedEventId = eventToDelete.id;
    setEventToDelete(null);

    try {
      const { error } = await supabase
        .from('app_events')
        .delete()
        .eq('id', deletedEventId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Event deleted successfully",
      });

      // Clear other local state for this event
      setLocalEventDescriptions(prev => {
        const newMap = new Map(prev);
        newMap.delete(deletedEventId);
        return newMap;
      });
      setLocalVerifiedEvents(prev => {
        const newMap = new Map(prev);
        newMap.delete(deletedEventId);
        return newMap;
      });

      // Refresh to ensure consistency with server
      onRefresh();
    } catch (error: any) {
      console.error('Error deleting event:', error);

      // Rollback optimistic update on error
      setLocalDeletedEventIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(deletedEventId);
        return newSet;
      });

      toast({
        title: "Error",
        description: `Failed to delete event: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      // 1. Get diff details to find associated events
      const { data: diffDetails, error: diffDetailsError } = await supabase
        .from('app_news')
        .select('page_id, v_before, v_after')
        .eq('id', diff.id)
        .single();

      if (diffDetailsError || !diffDetails) {
        throw new Error('Diff not found');
      }

      // 2. Delete associated events first (linked by page_id, v_before, v_after)
      const { error: deleteEventsError } = await supabase
        .from('app_events')
        .delete()
        .eq('page_id', diffDetails.page_id)
        .eq('v_before', diffDetails.v_before)
        .eq('v_after', diffDetails.v_after);

      if (deleteEventsError) {
        throw new Error(`Failed to delete associated events: ${deleteEventsError.message}`);
      }

      // 3. Delete the diff itself
      const { error: deleteError } = await supabase
        .from('app_news')
        .delete()
        .eq('id', diff.id);

      if (deleteError) {
        throw new Error(`Failed to delete diff: ${deleteError.message}`);
      }

      toast({
        title: "Success",
        description: "Diff and associated events deleted successfully",
      });
      onRefresh();
    } catch (err: any) {
      console.error('Error deleting diff:', err);
      toast({
        title: "Error",
        description: err.message || "An unexpected error occurred while deleting the diff",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  // Inline editing functions
  const startEditing = (e: React.MouseEvent, field: EditingField, value: string, newsId?: string, eventId?: string) => {
    e.stopPropagation();
    setEditingState({ field, value, newsId, eventId });
  };

  const cancelEditing = () => {
    setEditingState(null);
    setAiInstructions('');
  };

  const saveEdit = async () => {
    if (!editingState) return;

    const trimmedValue = editingState.value.trim();
    if (!trimmedValue) {
      toast({
        title: "Error",
        description: "Value cannot be empty",
        variant: "destructive",
      });
      return;
    }

    // Check character limits
    const maxLength = editingState.field === 'event_description' ? 280 : 500;
    if (trimmedValue.length > maxLength) {
      toast({
        title: "Error",
        description: `Text must be ${maxLength} characters or less`,
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      if (editingState.field === 'hook' || editingState.field === 'news_description') {
        // Update app_news table
        const updateField = editingState.field === 'hook' ? 'hook' : 'description';
        const { error } = await supabase
          .from('app_news')
          .update({ [updateField]: trimmedValue })
          .eq('id', editingState.newsId);

        if (error) throw error;

        // Optimistic update
        if (editingState.field === 'hook') {
          setLocalHook(trimmedValue);
        } else {
          setLocalNewsDescription(trimmedValue);
        }
      } else if (editingState.field === 'event_description') {
        // Update app_events table
        const { error } = await supabase
          .from('app_events')
          .update({ description: trimmedValue })
          .eq('id', editingState.eventId);

        if (error) throw error;

        // Optimistic update
        if (editingState.eventId) {
          setLocalEventDescriptions(prev => {
            const newMap = new Map(prev);
            newMap.set(editingState.eventId, trimmedValue);
            return newMap;
          });
        }
      }

      toast({
        title: "Success",
        description: "Updated successfully",
      });

      cancelEditing();
    } catch (error: any) {
      console.error('Error saving edit:', error);
      toast({
        title: "Error",
        description: `Failed to save: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAICleanup = async () => {
    if (!editingState || !editingState.value.trim()) {
      toast({
        title: "Error",
        description: "Please enter some text first",
        variant: "destructive",
      });
      return;
    }

    setIsAIProcessing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const fieldMapping = {
        'hook': 'hook',
        'news_description': 'description',
        'event_description': 'event_description',
      };

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-writer`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            field: fieldMapping[editingState.field],
            context: editingState.value,
            isRewrite: true,
            slug: diff.slug,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'AI request failed');
      }

      const data = await response.json();
      setEditingState({ ...editingState, value: data.text });
    } catch (error: any) {
      console.error('AI cleanup error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to process AI cleanup",
        variant: "destructive",
      });
    } finally {
      setIsAIProcessing(false);
    }
  };

  const handleAIWithInstructions = async () => {
    if (!aiInstructions.trim() || !editingState) {
      toast({
        title: "Error",
        description: "Please enter instructions",
        variant: "destructive",
      });
      return;
    }

    setIsAIProcessing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const fieldMapping = {
        'hook': 'hook',
        'news_description': 'description',
        'event_description': 'event_description',
      };

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-writer`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            field: fieldMapping[editingState.field],
            context: editingState.value,
            customInstructions: aiInstructions,
            slug: diff.slug,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'AI request failed');
      }

      const data = await response.json();
      setEditingState({ ...editingState, value: data.text });
      setAiInstructions('');
    } catch (error: any) {
      console.error('AI instruction error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to process AI instructions",
        variant: "destructive",
      });
    } finally {
      setIsAIProcessing(false);
    }
  };

  const handleAIRewriteAll = async () => {
    if (!diff.id || isAIRewriting) return;

    setIsAIRewriting(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      // Get non-deleted events from this diff
      const events = (diff.changes || []).filter((e: any) => !localDeletedEventIds.has(e.id));

      // Build context from events
      const eventsContext = events.length > 0
        ? events.map((e: any) => {
            const type = e.category ? `[${e.category}]` : '';
            const desc = e.description || 'No description';
            return `${type} ${desc}`.trim();
          }).join('; ')
        : 'No events recorded';

      // Call AI for hook
      const hookResponse = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-writer`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            field: 'hook',
            context: `Pricing page changes: ${eventsContext}`,
            slug: diff.slug,
          }),
        }
      );

      if (!hookResponse.ok) {
        const errorData = await hookResponse.json();
        throw new Error(errorData.error || 'Hook AI request failed');
      }
      const hookData = await hookResponse.json();

      // Call AI for description
      const descResponse = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-writer`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            field: 'description',
            context: `Pricing page changes: ${eventsContext}`,
            slug: diff.slug,
          }),
        }
      );

      if (!descResponse.ok) {
        const errorData = await descResponse.json();
        throw new Error(errorData.error || 'Description AI request failed');
      }
      const descData = await descResponse.json();

      // Save both to database
      const { error } = await supabase
        .from('app_news')
        .update({
          hook: hookData.text,
          description: descData.text
        })
        .eq('id', diff.id);

      if (error) throw error;

      // Optimistic updates
      setLocalHook(hookData.text);
      setLocalNewsDescription(descData.text);

      toast({
        title: "Success",
        description: "Hook and description rewritten successfully",
      });

    } catch (error: any) {
      console.error('AI rewrite error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to rewrite with AI",
        variant: "destructive",
      });
    } finally {
      setIsAIRewriting(false);
    }
  };

  // Click outside detection
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (editingState && editContainerRef.current && !editContainerRef.current.contains(event.target as Node)) {
        // User clicked outside - show save/cancel prompt
        setPendingAction(() => () => {
          // This will be called if user chooses to discard changes
          cancelEditing();
        });
        setShowUnsavedDialog(true);
      }
    };

    if (editingState) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [editingState]);

  const handleUnsavedSave = async () => {
    setShowUnsavedDialog(false);
    await saveEdit();
  };

  const handleUnsavedCancel = () => {
    setShowUnsavedDialog(false);
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  };

  const getEventTypeBadgeColor = (eventType: string | undefined): string => {
    const type = eventType?.toLowerCase() || '';
    if (type.includes('price')) return 'bg-green-100 text-green-800 border-green-300';
    if (type.includes('plan')) return 'bg-blue-100 text-blue-800 border-blue-300';
    if (type.includes('feature')) return 'bg-purple-100 text-purple-800 border-purple-300';
    if (type.includes('discount')) return 'bg-orange-100 text-orange-800 border-orange-300';
    if (type.includes('metric')) return 'bg-teal-100 text-teal-800 border-teal-300';
    return 'bg-gray-100 text-gray-800 border-gray-300';
  };

  // Filter events based on selected categories and exclude locally deleted events
  const allEvents = diff.changes || [];
  const filteredEvents = (selectedEventTypes.length > 0
    ? allEvents.filter((e: any) => selectedEventTypes.includes(e.category ?? 'null'))
    : allEvents
  ).filter((e: any) => !localDeletedEventIds.has(e.id));

  // Sort events by category priority: pricing_structure, feature, limits, misc, then others
  const categoryPriority: Record<string, number> = {
    'pricing_structure': 1,
    'feature': 2,
    'limits': 3,
    'misc': 4,
  };

  const sortedFilteredEvents = [...filteredEvents].sort((a: any, b: any) => {
    const aCat = a.category ?? 'null';
    const bCat = b.category ?? 'null';
    const aPriority = categoryPriority[aCat] ?? 999;
    const bPriority = categoryPriority[bCat] ?? 999;
    return aPriority - bPriority;
  });

  // Enrich filtered events with parent diff data for lightbox navigation
  const enrichedEvents = sortedFilteredEvents.map(event => ({
    ...event,
    // Don't override event's URLs - they're already correct from the view
    // Each event has its own asset_id with its own URLs
    logo_url: diff.company_logo_url,
  }));

  // Get unique categories from filtered events
  const uniqueChanges = Array.from(
    new Set(sortedFilteredEvents.map((c: any) => c.category))
  ).filter(Boolean);

  // Get diff type badge - show the actual period if available
  const getDiffType = () => {
    // If we have a period field, use that directly
    if (diff.period) {
      return { label: diff.period, color: 'bg-gray-100 text-gray-700 border-gray-300' };
    }

    // Fallback: Try to calculate period from dates
    if (diff.v_before && diff.v_after) {
      try {
        const beforeDate = new Date(
          parseInt(diff.v_before.substring(0, 4)),
          parseInt(diff.v_before.substring(4, 6)) - 1,
          parseInt(diff.v_before.substring(6, 8))
        );
        const afterDate = new Date(
          parseInt(diff.v_after.substring(0, 4)),
          parseInt(diff.v_after.substring(4, 6)) - 1,
          parseInt(diff.v_after.substring(6, 8))
        );

        // Calculate quarters for both dates
        const afterYear = afterDate.getFullYear();
        const afterMonth = afterDate.getMonth() + 1; // 0-indexed, so add 1
        const afterQuarter = Math.ceil(afterMonth / 3);

        const beforeYear = beforeDate.getFullYear();
        const beforeMonth = beforeDate.getMonth() + 1;
        const beforeQuarter = Math.ceil(beforeMonth / 3);

        // Only label as quarterly if v_before and v_after are in different quarters
        if (beforeYear !== afterYear || beforeQuarter !== afterQuarter) {
          return { label: `${afterYear}Q${afterQuarter}`, color: 'bg-gray-100 text-gray-700 border-gray-300' };
        }

        // Same quarter = Custom
        return { label: 'Custom', color: 'bg-blue-100 text-blue-700 border-blue-300' };
      } catch (e) {
        console.error('Error calculating period:', e);
      }
    }

    // Final fallback: return unknown
    return { label: 'Unknown', color: 'bg-gray-50 text-gray-500 border-gray-200' };
  };

  const diffType = getDiffType();
  const events = sortedFilteredEvents;

  // Always use only this card's enriched events for the lightbox
  // This ensures the lightbox only shows events from this specific news item
  const eventsForLightbox = enrichedEvents;

  // Get the first event ID from this specific diff (if any events exist)
  const firstEventId = enrichedEvents.length > 0 ? enrichedEvents[0].id : undefined;

  const getCharLimit = (field: EditingField) => {
    return field === 'event_description' ? 280 : 500;
  };

  // Handle edit button - navigate to edit page
  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit?.(); // Call callback to set navigation list before navigating
    navigate(`/diffs/${diff.id}/edit`);
  };

  // Handle preview button - open preview URL in new tab
  const handlePreview = (e: React.MouseEvent) => {
    e.stopPropagation();
    const afterDate = diff.v_after?.match(/(\d{8})/)?.[1] || diff.v_after;
    const beforeDate = diff.v_before?.match(/(\d{8})/)?.[1] || diff.v_before;
    const url = `https://pricingsaas.com/news/${diff.slug}/${afterDate}/?v_before=${beforeDate}&v3=true`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <Card
        id={`diff-${diff.id}`}
        className="hover:shadow-md transition-shadow"
      >
        <CardContent className="p-6">
          <div className="flex items-start gap-4 relative">
            {/* Left section - now takes more space */}
            <div className="flex-1 min-w-0 max-w-full">
              <div className="flex items-center gap-3 mb-3">
                {diff.company_logo_url && (
                  <img
                    src={diff.company_logo_url}
                    alt={`${diff.slug} logo`}
                    className="h-8 w-8 rounded object-contain flex-shrink-0 bg-white border border-gray-200"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                )}
                <h3 className="text-lg font-semibold text-gray-900">
                  {diff.slug}
                </h3>
                {/* Period badge with tooltip */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Badge variant="outline" className={`text-xs font-medium px-2 py-0.5 flex-shrink-0 ${diffType.color}`}>
                        {diffType.label}
                      </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-xs">
                        {formatVersionDate(diff.v_before)} → {formatVersionDate(diff.v_after)}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                {/* Date range - visible without hover */}
                {diff.v_before && diff.v_after && (
                  <span className="text-xs text-gray-500 ml-auto">
                    {formatVersionDateAbbreviated(diff.v_before)} → {formatVersionDateAbbreviated(diff.v_after)}
                  </span>
                )}
              </div>

              {/* Hook - Inline Editable */}
              {(localHook || diff.hook) && (
                <div className="mb-3">
                  {editingState?.field === 'hook' && editingState?.newsId === diff.id ? (
                    <div ref={editContainerRef} className="space-y-1" onClick={(e) => e.stopPropagation()}>
                      <div className="relative">
                        <textarea
                          value={editingState.value}
                          onChange={(e) => setEditingState({ ...editingState, value: e.target.value })}
                          className="w-full px-3 py-2 pr-16 text-base font-medium bg-white border border-blue-300 rounded text-gray-800 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                          rows={2}
                          maxLength={getCharLimit('hook')}
                          autoFocus
                        />
                        <div className="absolute top-1 right-1 flex items-center gap-0.5">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={cancelEditing}
                            disabled={isSaving}
                            className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600"
                            title="Cancel"
                          >
                            <XCircle className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={saveEdit}
                            disabled={isSaving || !editingState.value.trim()}
                            className="h-6 w-6 p-0 text-green-500 hover:text-green-600"
                            title="Save"
                          >
                            {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                          </Button>
                        </div>
                        <div className="absolute bottom-1 right-1 text-xs text-gray-400">
                          {editingState.value.length}/{getCharLimit('hook')}
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 text-xs text-purple-500 hover:text-purple-600"
                            disabled={isAIProcessing}
                          >
                            {isAIProcessing ? (
                              <>
                                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                Processing...
                              </>
                            ) : (
                              <>
                                <Sparkles className="h-3 w-3 mr-1" />
                                AI Assist
                              </>
                            )}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-64">
                          <div className="p-2 space-y-2">
                            <DropdownMenuItem
                              onClick={handleAICleanup}
                              disabled={isAIProcessing}
                              className="cursor-pointer text-xs"
                            >
                              <Sparkles className="h-3 w-3 mr-2 text-purple-400" />
                              <div>
                                <div className="font-medium">Cleanup</div>
                                <div className="text-xs text-gray-500">Improve clarity</div>
                              </div>
                            </DropdownMenuItem>
                            <div className="px-2 py-1 border-t border-gray-200">
                              <div className="text-xs text-gray-500 mb-1">Custom instructions:</div>
                              <input
                                type="text"
                                value={aiInstructions}
                                onChange={(e) => setAiInstructions(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && aiInstructions.trim()) {
                                    e.preventDefault();
                                    handleAIWithInstructions();
                                  }
                                }}
                                className="w-full px-2 py-1 bg-gray-50 border border-gray-300 rounded text-xs"
                                placeholder="e.g., 'make it shorter'"
                                disabled={isAIProcessing}
                              />
                              {aiInstructions.trim() && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={handleAIWithInstructions}
                                  disabled={isAIProcessing}
                                  className="mt-1 h-6 text-xs w-full text-purple-500"
                                >
                                  Apply
                                </Button>
                              )}
                            </div>
                          </div>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ) : (
                    <div className="group hover:bg-gray-50 rounded px-2 py-1 -mx-2 transition-colors">
                      <div className="flex items-start gap-2">
                        <p className="text-base text-gray-800 font-medium flex-1 line-clamp-2">
                          {localHook || diff.hook}
                        </p>

                        {/* AI Rewrite Button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAIRewriteAll();
                          }}
                          disabled={isAIRewriting}
                          title="AI rewrite hook and description"
                        >
                          {isAIRewriting ? (
                            <Loader2 className="h-3.5 w-3.5 text-purple-500 animate-spin" />
                          ) : (
                            <Sparkles className="h-3.5 w-3.5 text-purple-500 hover:text-purple-600" />
                          )}
                        </Button>

                        {/* Edit Button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                          onClick={(e) => startEditing(e, 'hook', localHook || diff.hook, diff.id)}
                          title="Edit"
                        >
                          <Edit3 className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* News Description - Inline Editable */}
              {(localNewsDescription || diff.description) && (
                <div className="mb-3">
                  {editingState?.field === 'news_description' && editingState?.newsId === diff.id ? (
                    <div ref={editContainerRef} className="space-y-1" onClick={(e) => e.stopPropagation()}>
                      <div className="relative">
                        <textarea
                          value={editingState.value}
                          onChange={(e) => setEditingState({ ...editingState, value: e.target.value })}
                          className="w-full px-3 py-2 pr-16 text-sm bg-white border border-blue-300 rounded text-gray-600 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                          rows={2}
                          maxLength={getCharLimit('news_description')}
                          autoFocus
                        />
                        <div className="absolute top-1 right-1 flex items-center gap-0.5">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={cancelEditing}
                            disabled={isSaving}
                            className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600"
                            title="Cancel"
                          >
                            <XCircle className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={saveEdit}
                            disabled={isSaving || !editingState.value.trim()}
                            className="h-6 w-6 p-0 text-green-500 hover:text-green-600"
                            title="Save"
                          >
                            {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                          </Button>
                        </div>
                        <div className="absolute bottom-1 right-1 text-xs text-gray-400">
                          {editingState.value.length}/{getCharLimit('news_description')}
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 text-xs text-purple-500 hover:text-purple-600"
                            disabled={isAIProcessing}
                          >
                            {isAIProcessing ? (
                              <>
                                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                Processing...
                              </>
                            ) : (
                              <>
                                <Sparkles className="h-3 w-3 mr-1" />
                                AI Assist
                              </>
                            )}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-64">
                          <div className="p-2 space-y-2">
                            <DropdownMenuItem
                              onClick={handleAICleanup}
                              disabled={isAIProcessing}
                              className="cursor-pointer text-xs"
                            >
                              <Sparkles className="h-3 w-3 mr-2 text-purple-400" />
                              <div>
                                <div className="font-medium">Cleanup</div>
                                <div className="text-xs text-gray-500">Improve clarity</div>
                              </div>
                            </DropdownMenuItem>
                            <div className="px-2 py-1 border-t border-gray-200">
                              <div className="text-xs text-gray-500 mb-1">Custom instructions:</div>
                              <input
                                type="text"
                                value={aiInstructions}
                                onChange={(e) => setAiInstructions(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && aiInstructions.trim()) {
                                    e.preventDefault();
                                    handleAIWithInstructions();
                                  }
                                }}
                                className="w-full px-2 py-1 bg-gray-50 border border-gray-300 rounded text-xs"
                                placeholder="e.g., 'add more details'"
                                disabled={isAIProcessing}
                              />
                              {aiInstructions.trim() && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={handleAIWithInstructions}
                                  disabled={isAIProcessing}
                                  className="mt-1 h-6 text-xs w-full text-purple-500"
                                >
                                  Apply
                                </Button>
                              )}
                            </div>
                          </div>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ) : (
                    <div className="group hover:bg-gray-50 rounded px-2 py-1 -mx-2 transition-colors">
                      <div className="flex items-start gap-2">
                        <p className="text-sm text-gray-600 flex-1 line-clamp-3">
                          {localNewsDescription || diff.description}
                        </p>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                          onClick={(e) => startEditing(e, 'news_description', localNewsDescription || diff.description, diff.id)}
                          title="Edit"
                        >
                          <Edit3 className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Events List - Always Visible */}
              {events.length > 0 && (
                <div className="space-y-1.5">
                  {events.map((event: any) => {
                    const description = localEventDescriptions.get(event.id) || event.description;
                    const isEditing = editingState?.field === 'event_description' && editingState?.eventId === event.id;

                    return (
                      <div key={event.id}>
                        {isEditing ? (
                          <div ref={editContainerRef} className="space-y-1 p-2 bg-white border border-blue-300 rounded" onClick={(e) => e.stopPropagation()}>
                            <div className="relative">
                              <textarea
                                value={editingState.value}
                                onChange={(e) => setEditingState({ ...editingState, value: e.target.value })}
                                className="w-full px-3 py-2 pr-16 text-sm bg-white border border-blue-300 rounded text-gray-700 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                                rows={2}
                                maxLength={280}
                                autoFocus
                              />
                              <div className="absolute top-1 right-1 flex items-center gap-0.5">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={cancelEditing}
                                  disabled={isSaving}
                                  className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600"
                                  title="Cancel"
                                >
                                  <XCircle className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={saveEdit}
                                  disabled={isSaving || !editingState.value.trim()}
                                  className="h-6 w-6 p-0 text-green-500 hover:text-green-600"
                                  title="Save"
                                >
                                  {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                                </Button>
                              </div>
                              <div className="absolute bottom-1 right-1 text-xs text-gray-400">
                                {editingState.value.length}/280
                              </div>
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 text-xs text-purple-500 hover:text-purple-600"
                                  disabled={isAIProcessing}
                                >
                                  {isAIProcessing ? (
                                    <>
                                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                      Processing...
                                    </>
                                  ) : (
                                    <>
                                      <Sparkles className="h-3 w-3 mr-1" />
                                      AI Assist
                                    </>
                                  )}
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="start" className="w-64">
                                <div className="p-2 space-y-2">
                                  <DropdownMenuItem
                                    onClick={handleAICleanup}
                                    disabled={isAIProcessing}
                                    className="cursor-pointer text-xs"
                                  >
                                    <Sparkles className="h-3 w-3 mr-2 text-purple-400" />
                                    <div>
                                      <div className="font-medium">Cleanup</div>
                                      <div className="text-xs text-gray-500">Improve clarity</div>
                                    </div>
                                  </DropdownMenuItem>
                                  <div className="px-2 py-1 border-t border-gray-200">
                                    <div className="text-xs text-gray-500 mb-1">Custom instructions:</div>
                                    <input
                                      type="text"
                                      value={aiInstructions}
                                      onChange={(e) => setAiInstructions(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' && aiInstructions.trim()) {
                                          e.preventDefault();
                                          handleAIWithInstructions();
                                        }
                                      }}
                                      className="w-full px-2 py-1 bg-gray-50 border border-gray-300 rounded text-xs"
                                      placeholder="e.g., 'make it concise'"
                                      disabled={isAIProcessing}
                                    />
                                    {aiInstructions.trim() && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleAIWithInstructions}
                                        disabled={isAIProcessing}
                                        className="mt-1 h-6 text-xs w-full text-purple-500"
                                      >
                                        Apply
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        ) : (
                          <div
                            className="group flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded transition-colors cursor-pointer"
                            onClick={() => {
                              // Open lightbox with sorted events from THIS diff (same order as displayed)
                              onOpenLightbox(diff.news_id, event.id, diff.slug, enrichedEvents);
                            }}
                          >
                            {/* Verified checkbox */}
                            <Button
                              variant="ghost"
                              size="sm"
                              className={`h-4 w-4 p-0 flex-shrink-0 ${
                                localVerifiedEvents.get(event.id) ?? event.verified
                                  ? 'text-green-600 hover:text-green-700'
                                  : 'text-gray-300 hover:text-gray-400'
                              }`}
                              onClick={(e) => handleToggleEventVerified(e, event.id, localVerifiedEvents.get(event.id) ?? event.verified)}
                              title={localVerifiedEvents.get(event.id) ?? event.verified ? 'Verified' : 'Not verified'}
                            >
                              {localVerifiedEvents.get(event.id) ?? event.verified ? (
                                <CheckCircle2 className="h-4 w-4" />
                              ) : (
                                <Circle className="h-4 w-4" />
                              )}
                            </Button>

                            {/* Category badge */}
                            <Badge
                              variant="outline"
                              className={`text-xs px-1.5 py-0 flex-shrink-0 ${getCategoryColor(event.category)}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {formatCategoryLabel(event.category)}
                            </Badge>

                            {/* Description with tooltip */}
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <p className="text-sm text-gray-700 flex-1 truncate min-w-0">
                                    {description || 'No description'}
                                  </p>
                                </TooltipTrigger>
                                {description && description.length > 50 && (
                                  <TooltipContent side="top" className="max-w-sm">
                                    <p className="text-xs">{description}</p>
                                  </TooltipContent>
                                )}
                              </Tooltip>
                            </TooltipProvider>

                            {/* Edit button */}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-gray-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                startEditing(e, 'event_description', description || '', undefined, event.id);
                              }}
                              title="Edit"
                            >
                              <Edit3 className="h-3.5 w-3.5" />
                            </Button>

                            {/* Delete event button */}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEventToDelete(event);
                                setDeleteEventDialogOpen(true);
                              }}
                              title="Delete event"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Grouped tags from all events - displayed at bottom */}
              {(() => {
                // Collect all unique tags from all filtered events
                const allTags = new Set<string>();
                events.forEach((event: any) => {
                  if (event.tags && Array.isArray(event.tags)) {
                    event.tags.forEach((tag: string) => allTags.add(tag));
                  }
                });

                return allTags.size > 0 ? (
                  <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-gray-100">
                    {Array.from(allTags).map((tag: string) => (
                      <Badge
                        key={tag}
                        variant="outline"
                        className="text-xs px-2 py-0.5 bg-gray-50 text-gray-600 border-gray-300 hover:bg-gray-100 rounded-sm"
                      >
                        {formatTagLabel(tag as PricingEventTag)}
                      </Badge>
                    ))}
                  </div>
                ) : null;
              })()}
            </div>

            {/* Right section: Action buttons - compact */}
            <div className="flex items-center gap-1 flex-shrink-0">
              {/* Edit button */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-2.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                      onClick={handleEdit}
                    >
                      <Edit3 className="h-3.5 w-3.5 mr-1" />
                      <span className="text-xs">Edit</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Edit annotations</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {/* Preview - full button */}
              <Button
                variant="outline"
                size="sm"
                className="h-8 px-2.5 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                onClick={handlePreview}
                title="Preview on PricingSaaS"
              >
                <ExternalLink className="h-3.5 w-3.5 mr-1" />
                <span className="text-xs">Preview</span>
              </Button>
              {/* Fix Diff - icon button */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`h-8 w-8 relative ${
                        fixDiffTask
                          ? 'text-amber-500 bg-amber-50'
                          : 'text-gray-500 hover:text-amber-600 hover:bg-amber-50'
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!fixDiffTask) {
                          setFixDiffModalOpen(true);
                        }
                      }}
                      disabled={!!fixDiffTask}
                    >
                      {fixDiffTask ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Wrench className="h-3.5 w-3.5" />
                      )}
                      {fixDiffTask && (
                        <span className="absolute -top-1 -right-1 w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {fixDiffTask ? (
                      <div className="text-xs">
                        <p className="font-medium">Fix in progress...</p>
                        <p className="text-gray-400">Status: {fixDiffTask.status}</p>
                      </div>
                    ) : (
                      'Fix diff with Claude'
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {/* Published/Draft badge indicator */}
              {diff.published ? (
                <Badge className="text-xs bg-green-100 text-green-700 border-green-200 pointer-events-none">● Published</Badge>
              ) : (
                <Badge className="text-xs bg-gray-100 text-gray-500 border-gray-200 pointer-events-none">○ Draft</Badge>
              )}
              {/* Publish/Unpublish - icon button */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={`h-8 w-8 ${diff.published ? 'text-green-600 hover:text-green-700 hover:bg-green-50' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50'}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTogglePublished(e);
                      }}
                    >
                      {diff.published ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{diff.published ? 'Published - Click to unpublish' : 'Unpublished - Click to publish'}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {/* Delete */}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-gray-500 hover:text-red-600"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteDialogOpen(true);
                }}
                title="Delete diff"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
              {/* Diff Viewer (Debug) - icon button, last position */}
              {hasAnnotationData(diff.changes) && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-purple-500 hover:text-purple-600 hover:bg-purple-50"
                        onClick={handleTestDiffViewerPreview}
                      >
                        <Bug className="h-3.5 w-3.5" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Debug: View in Diff Viewer</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>

            {/* Timestamp - bottom right */}
            <div className="absolute bottom-4 right-6 flex items-center gap-1 text-xs text-gray-400">
              <Calendar className="h-3 w-3" />
              <span>{formatRelativeTime(diff.created_at)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete this diff?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the diff for <strong>{diff.slug}</strong>
              {' '}(from {diff.v_before ? formatVersionDate(diff.v_before) : 'unknown'} to{' '}
              {diff.v_after ? formatVersionDate(diff.v_after) : 'unknown'}).
              <br /><br />
              {events.length > 0 && (
                <>
                  This will also delete <strong>{events.length} associated event{events.length !== 1 ? 's' : ''}</strong>.
                  <br /><br />
                </>
              )}
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Unsaved Changes Dialog */}
      <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Save changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Would you like to save them before closing?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleUnsavedCancel}>Discard</AlertDialogCancel>
            <AlertDialogAction onClick={handleUnsavedSave} className="bg-blue-600 hover:bg-blue-700">
              Save
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Verify All Dialog */}
      <AlertDialog open={verifyAllDialogOpen} onOpenChange={setVerifyAllDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>No verified events</AlertDialogTitle>
            <AlertDialogDescription>
              None of the events in this diff have been verified yet.
              <br /><br />
              Would you like to verify all <strong>{events.length} event{events.length !== 1 ? 's' : ''}</strong> and publish, or cancel?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleVerifyAllAndPublish}
              className="bg-green-600 hover:bg-green-700"
            >
              Verify All & Publish
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Event Dialog */}
      <AlertDialog open={deleteEventDialogOpen} onOpenChange={setDeleteEventDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this event?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the event:
              <br /><br />
              <strong>{eventToDelete?.description || 'Untitled event'}</strong>
              <br /><br />
              {eventToDelete?.category && (
                <>
                  Category: <strong>{eventToDelete.category}</strong>
                  <br /><br />
                </>
              )}
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteEvent}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Event
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Fix Diff Modal */}
      <FixDiffModal
        open={fixDiffModalOpen}
        onOpenChange={setFixDiffModalOpen}
        diffId={diff.id}
        slug={diff.slug}
        period={diff.v_after || diff.v_before || ''}
        hook={localHook ?? diff.hook}
        events={sortedFilteredEvents.map((change: any) => ({
          id: change.id || String(Math.random()),
          description: change.description,
          category: change.category,
        }))}
        onTaskCreated={onRefresh}
      />
    </>
  );
};
